/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop at build time. */
import { css } from '@firebolt-dev/css'
import { useEffect, useState } from 'react'
import { CheckIcon, CopyIcon, KeyRoundIcon, RotateCcwIcon, ShieldCheckIcon } from 'lucide-react'

import { COLORS, ICON_SIZES, RADII, SHADOWS, TYPE, space } from '../theme'
import { exportGuestRecovery, recoverGuestSession } from '../guest-session'
import { Btn, IdentityBadge, Panel, ScreenShell } from './VoiceKit'

export function GuestSecurityScreen({ world, onClose }) {
  const [code, setCode] = useState('')
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(null)

  useEffect(() => {
    const onKey = event => { if (event.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const exportCode = async () => {
    const confirmed = await world.ui.confirm({ kind: 'security', tone: 'warning', title: 'СОЗДАТЬ НОВЫЙ КОД?', message: 'Предыдущий код восстановления перестанет действовать. Новый код будет показан только в этом окне.', safeEnter: false, dedupeKey: 'security:export-confirm' })
    if (!confirmed) return
    setBusy('export')
    try {
      setCode(await exportGuestRecovery())
      world.ui.notify({ kind: 'security', tone: 'success', title: 'КОД СОЗДАН', message: 'Сохрани новый код вне браузера. Его значение не добавлено в уведомление.', dedupeKey: 'security:exported' })
    } catch (err) {
      world.ui.notify({ kind: 'error', title: 'КОД НЕ СОЗДАН', message: 'Не удалось создать код восстановления.', dedupeKey: 'security:export-error' })
    } finally { setBusy(null) }
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      world.ui.notify({ kind: 'success', title: 'КОД СКОПИРОВАН', message: 'Сохрани его в приватном месте.', dedupeKey: 'security:copied' })
    } catch (err) {
      world.ui.notify({ kind: 'warning', title: 'КОПИРОВАНИЕ НЕДОСТУПНО', message: 'Браузер не разрешил копирование. Выдели код вручную.', dedupeKey: 'security:copy-error' })
    }
  }

  const recover = async () => {
    if (!input.trim()) return
    const confirmed = await world.ui.confirm({ kind: 'security', tone: 'warning', title: 'ЗАМЕНИТЬ ГОСТЕВУЮ СЕССИЮ?', message: 'После серверной проверки текущая guest-сессия будет заменена восстановленным персонажем.', safeEnter: false, dedupeKey: 'security:recover-confirm' })
    if (!confirmed) return
    setBusy('recover')
    try {
      await recoverGuestSession(input)
      world.ui.notify({ kind: 'security', tone: 'success', title: 'ПЕРСОНАЖ ВОССТАНОВЛЕН', message: 'Сервер подтвердил новую сессию. Мир будет перезагружен.', dedupeKey: 'security:recovered', durable: true })
      window.location.reload()
    } catch (err) {
      world.ui.notify({ kind: 'error', title: 'ВОССТАНОВЛЕНИЕ ОТКЛОНЕНО', message: 'Код недействителен, истёк или уже использован.', dedupeKey: 'security:recover-error' })
      setBusy(null)
    }
  }

  return <ScreenShell
    label='SECURITY'
    title='ГОСТЕВОЙ АККАУНТ'
    subtitle='Сессия защищена HttpOnly cookie; секреты не попадают в адрес мира'
    ariaLabel='Безопасность гостевого аккаунта'
    onClose={onClose}
    meta={<IdentityBadge title='SESSION PROTECTED' subtitle='Одноразовый WebSocket ticket' />}
  >
    <div css={css`flex:1;min-height:0;overflow-y:auto;padding:${space(3)};display:grid;grid-template-columns:1fr 1fr;gap:${space(2)};align-content:start;@media(max-width:800px){grid-template-columns:1fr;padding:${space(1)};}`}>
      <Panel title='ЭКСПОРТ КОДА ВОССТАНОВЛЕНИЯ' info='Код показывается только сейчас. Сервер хранит только его криптографический отпечаток.'>
        <div css={stackCss}>
          <ShieldCheckIcon size={ICON_SIZES.xl} color={COLORS.success} />
          <p css={noteCss}>Сохрани код вне браузера. Он одноразовый и восстанавливает именно текущего гостевого персонажа.</p>
          <Btn full disabled={busy === 'export'} onClick={exportCode}><KeyRoundIcon size={ICON_SIZES.xs} /> {busy === 'export' ? 'СОЗДАЁМ…' : 'СОЗДАТЬ КОД'}</Btn>
          {code && <div css={codeCss}><code>{code}</code><Btn size='small' onClick={copy}><CopyIcon size={ICON_SIZES.xs} /> КОПИРОВАТЬ</Btn></div>}
        </div>
      </Panel>
      <Panel title='ВОССТАНОВИТЬ ПЕРСОНАЖА' info='Успешное восстановление заменит текущую guest-сессию в этом браузере.'>
        <div css={stackCss}>
          <RotateCcwIcon size={ICON_SIZES.xl} color={COLORS.cyan} />
          <label css={labelCss}>КОД ВОССТАНОВЛЕНИЯ<textarea value={input} onChange={event => setInput(event.target.value)} placeholder='Вставь сохранённый код' css={fieldCss} /></label>
          <Btn full disabled={!input.trim() || busy === 'recover'} onClick={recover}><CheckIcon size={ICON_SIZES.xs} /> {busy === 'recover' ? 'ПРОВЕРЯЕМ…' : 'ВОССТАНОВИТЬ'}</Btn>
        </div>
      </Panel>
    </div>
  </ScreenShell>
}

const stackCss = css`display:flex;flex-direction:column;align-items:flex-start;gap:${space(2)};padding:${space(2)};`
const noteCss = css`margin:0;color:${COLORS.textSecondary};font-size:${TYPE.body.size};line-height:1.5;`
const labelCss = css`width:100%;display:flex;flex-direction:column;gap:${space(1)};font-size:${TYPE.caption.size};color:${COLORS.textSecondary};letter-spacing:.08em;`
const fieldCss = css`min-height:110px;resize:vertical;padding:${space(1)};border:1px solid ${COLORS.border};border-radius:${RADII.lg};background:${COLORS.bgPanelLight};color:${COLORS.text};font:inherit;&:focus{outline:none;border-color:${COLORS.borderStrong};box-shadow:${SHADOWS.focus};}`
const codeCss = css`width:100%;display:grid;gap:${space(1)};padding:${space(1)};border:1px solid ${COLORS.warning};border-radius:${RADII.lg};background:${COLORS.warningSoft};code{overflow-wrap:anywhere;color:${COLORS.text};user-select:text;}`
